import { useRef, useState, useEffect } from 'react'
import { ChevronDown, Plus, X } from 'lucide-react'
import {
  HORIZON_OPTIONS,
  REGION_OPTIONS,
  type Horizon,
  type RegionId,
  type SizingScope,
} from './sizingView'

interface Props {
  scope: SizingScope
  onChange: (next: SizingScope) => void
}

export function ScopeControls({ scope, onChange }: Props) {
  return (
    <div
      className="grid items-center gap-6 rounded-[16px] px-5 py-4 md:grid-cols-[1fr_auto_1fr]"
      style={{
        backgroundColor: 'var(--color-warm-50)',
      }}
    >
      <RegionField
        selected={scope.regions}
        onChange={(regions) => onChange({ ...scope, regions })}
      />

      <div
        className="hidden h-12 w-px md:block"
        style={{ backgroundColor: 'var(--color-border-subtle)' }}
        aria-hidden="true"
      />

      <TimeframeField
        baseYear={scope.baseYear}
        horizon={scope.horizon}
        onChangeBase={(baseYear) => onChange({ ...scope, baseYear })}
        onChangeHorizon={(horizon) => onChange({ ...scope, horizon })}
      />
    </div>
  )
}

function RegionField({
  selected,
  onChange,
}: {
  selected: RegionId[]
  onChange: (next: RegionId[]) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const unselected = REGION_OPTIONS.filter((r) => !selected.includes(r.id))

  const add = (id: RegionId) => {
    onChange([...selected, id])
    setOpen(false)
  }
  const remove = (id: RegionId) => {
    const next = selected.filter((s) => s !== id)
    onChange(next.length === 0 ? ['global'] : next)
  }

  return (
    <div>
      <FieldLabel>Region</FieldLabel>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {selected.map((id) => {
          const opt = REGION_OPTIONS.find((o) => o.id === id)
          if (!opt) return null
          return (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded-full pl-3 pr-1.5 py-0.5 text-[12px]"
              style={{
                backgroundColor: 'var(--color-market-50)',
                color: 'var(--color-market-700)',
              }}
            >
              {opt.label}
              <button
                type="button"
                onClick={() => remove(id)}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full transition-colors hover:bg-[var(--color-market-100)]"
                aria-label={`Remove ${opt.label}`}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          )
        })}
        <div className="relative" ref={ref}>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            disabled={unselected.length === 0}
            className="inline-flex items-center gap-1 rounded-full border border-dashed px-3 py-1 text-[11px] transition-colors hover:bg-[var(--color-warm-100)] disabled:opacity-40"
            style={{
              borderColor: 'var(--color-border-strong)',
              color: 'var(--color-text-muted)',
            }}
          >
            <Plus className="h-2.5 w-2.5" />
            add region
          </button>
          {open ? (
            <div
              className="absolute left-0 top-full z-30 mt-1 w-48 overflow-hidden rounded-md border shadow-md"
              style={{
                backgroundColor: 'var(--color-bg-card, white)',
                borderColor: 'var(--color-border-subtle)',
              }}
            >
              {unselected.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => add(o.id)}
                  className="block w-full px-3 py-1.5 text-left text-[12px] transition-colors hover:bg-[var(--color-warm-100)]"
                  style={{ color: 'var(--color-text)' }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function TimeframeField({
  baseYear,
  horizon,
  onChangeBase,
  onChangeHorizon,
}: {
  baseYear: number
  horizon: Horizon
  onChangeBase: (y: number) => void
  onChangeHorizon: (h: Horizon) => void
}) {
  const [baseOpen, setBaseOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const now = new Date().getFullYear()
  const yearOptions = [now - 1, now, now + 1]

  useEffect(() => {
    if (!baseOpen) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setBaseOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [baseOpen])

  return (
    <div>
      <FieldLabel>Timeframe</FieldLabel>
      <div className="mt-2 flex items-center gap-4 text-[12px]">
        <span
          className="inline-flex items-center gap-1.5"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          <span
            className="text-[10px] font-semibold uppercase tracking-[0.1em]"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Base
          </span>
          <div className="relative" ref={ref}>
            <button
              type="button"
              onClick={() => setBaseOpen((o) => !o)}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-semibold tabular-nums transition-colors hover:bg-[var(--color-warm-200)]"
              style={{ color: 'var(--color-text)' }}
            >
              {baseYear}
              <ChevronDown className="h-3 w-3" />
            </button>
            {baseOpen ? (
              <div
                className="absolute left-0 top-full z-30 mt-1 w-[80px] overflow-hidden rounded-md border shadow-md"
                style={{
                  backgroundColor: 'var(--color-bg-card, white)',
                  borderColor: 'var(--color-border-subtle)',
                }}
              >
                {yearOptions.map((y) => (
                  <button
                    key={y}
                    type="button"
                    onClick={() => {
                      onChangeBase(y)
                      setBaseOpen(false)
                    }}
                    className="block w-full px-2 py-1 text-left text-[12px] tabular-nums transition-colors hover:bg-[var(--color-warm-100)]"
                    style={{
                      color: y === baseYear ? 'var(--color-market)' : 'var(--color-text)',
                      fontWeight: y === baseYear ? 600 : 400,
                    }}
                  >
                    {y}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </span>

        <span
          aria-hidden="true"
          style={{ color: 'var(--color-border-strong)' }}
        >
          ·
        </span>

        <span
          className="inline-flex items-center gap-1.5"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          <span
            className="text-[10px] font-semibold uppercase tracking-[0.1em]"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Horizon
          </span>
          <div
            className="inline-flex overflow-hidden rounded-md border"
            style={{ borderColor: 'var(--color-border-subtle)' }}
          >
            {HORIZON_OPTIONS.map((h) => {
              const active = h === horizon
              return (
                <button
                  key={h}
                  type="button"
                  onClick={() => onChangeHorizon(h)}
                  className="px-3 py-1 text-[11px] font-medium transition-colors"
                  style={{
                    backgroundColor: active ? 'var(--color-text)' : 'transparent',
                    color: active ? 'white' : 'var(--color-text-secondary)',
                  }}
                >
                  {h}
                </button>
              )
            })}
          </div>
        </span>
      </div>
    </div>
  )
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="text-[10px] font-semibold uppercase tracking-[0.1em]"
      style={{ color: 'var(--color-text-muted)' }}
    >
      {children}
    </span>
  )
}
